/* Đối soát cọc (Kế toán): khớp sao kê → xác nhận đã nhận cọc → đẩy cọc qua POS (đơn hàng cọc) · hoàn / bỏ cọc — lưu trên máy chờ nối API */
import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Alert, Button, Empty, Input, Popconfirm, Segmented, Table, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { ArrowLeft, Clock3, Receipt, Search, Send, ShoppingBag } from "lucide-react";
import Layout from "../../Layout";
import useAuthStore from "../../store/authStore";
import { Kpi } from "./dashboard/ui";
import { DEP_ST, ORDER_ST, money, orderDue } from "./lead/quote";
import { currentStaffName, useLeadStore } from "./lead/store";
import { allDeposits, type DepositAction, type DepositRow } from "./lead/deposit";
import { AskButton } from "./lead/QuoteTab";
import { runDepositApi } from "./lead/api";

type St = "check" | "waiting" | "push" | "pushed" | "closed" | "all";
const ST_PASS: Record<St, (r: DepositRow) => boolean> = {
    check: (r) => r.deposit.status === "checking",
    waiting: (r) => r.deposit.status === "waiting" && r.quote.status !== "replaced" && r.quote.status !== "cancelled",
    push: (r) => r.deposit.status === "received" && !r.deposit.order,
    pushed: (r) => !!r.deposit.order,
    closed: (r) => r.deposit.status === "refunded" || r.deposit.status === "forfeited" || r.deposit.status === "cancelled" || r.deposit.status === "refund_pending",
    all: () => true,
};

export default function DepositReconcile() {
    const { isSuperAdmin, hasPermission } = useAuthStore();
    // Kế toán (tài chính) hoặc Người vận hành Telesales mới được xác nhận / đẩy POS / hoàn / bỏ cọc
    const canAct = isSuperAdmin || hasPermission("finance.view") || hasPermission("telesales.config");
    const byId = useLeadStore((s) => s.byId);
    const depositAct = useLeadStore((s) => s.depositAct);
    const by = useMemo(currentStaffName, []);

    const [st, setSt] = useState<St>("check");
    const [q, setQ] = useState("");
    const now = Date.now();

    const rows = useMemo(() => allDeposits(byId).sort((a, b) => (b.deposit.receiptAt ?? b.deposit.createdAt ?? b.quote.at) - (a.deposit.receiptAt ?? a.deposit.createdAt ?? a.quote.at)), [byId]);
    const sum = (list: DepositRow[], f: (r: DepositRow) => number) => list.reduce((s, r) => s + f(r), 0);
    const stats = {
        check: rows.filter(ST_PASS.check),
        waiting: rows.filter(ST_PASS.waiting),
        push: rows.filter(ST_PASS.push),
        pushed: rows.filter(ST_PASS.pushed),
    };
    const overdue = stats.waiting.filter((r) => r.deposit.due < now).length;

    const kw = q.trim().toLowerCase();
    const match = (...v: (string | null | undefined)[]) => !kw || v.some((x) => String(x ?? "").toLowerCase().includes(kw));
    const shown = rows.filter((r) => ST_PASS[st](r) && match(r.deposit.code, r.deposit.note, r.deposit.cust?.name, r.deposit.cust?.phone, r.deposit.cust?.code, r.quote.code, r.deposit.order?.code, r.deposit.by));

    const run = async (r: DepositRow, act: DepositAction, ok: string) => {
        // Khoản đã lên server: báo biên lai, chưa khớp, xác nhận phải qua API. Đẩy POS, hoàn, bỏ cọc chưa có API nên vẫn lưu trên máy
        const sid = r.deposit.serverId;
        const err = sid && (act.type === "receipt" || act.type === "reject" || act.type === "confirm")
            ? await runDepositApi(r.leadId, sid, act, by)
            : depositAct(r.leadId, r.quote.id, act, by);
        if (err) message.error(err); else message.success(ok);
    };

    const columns: ColumnsType<DepositRow> = [
        {
            title: "Mã cọc", key: "code", width: 120,
            render: (_, r) => <><div className="font-mono font-semibold">{r.deposit.code}</div><div className="text-[12px] text-gray-500">theo {r.quote.code}</div></>,
        },
        {
            title: "Khách", key: "cust", width: 170,
            render: (_, r) => (
                <>
                    <Link to={`/telesales/customer?q=${encodeURIComponent(r.deposit.cust?.name ?? "")}`} className="font-medium text-[#3e91ab] hover:underline">{r.deposit.cust?.name ?? `KH ${r.leadId}`}</Link>
                    <div className="text-[12px] text-gray-500">{r.deposit.cust?.code}{r.deposit.cust?.phone ? ` · ${String(r.deposit.cust.phone).replace(/(\d{3})\d+(\d{4})/, "$1***$2")}` : ""}</div>
                </>
            ),
        },
        { title: "Tiền cọc", key: "amount", width: 116, align: "right", render: (_, r) => <b>{money(r.deposit.amount)}</b> },
        { title: "Nội dung CK", key: "note", width: 170, render: (_, r) => <Tag className="!m-0 font-mono" bordered={false}>{r.deposit.note}</Tag> },
        {
            title: "Hạn CK", key: "due", width: 92,
            render: (_, r) => {
                const late = r.deposit.status === "waiting" && r.deposit.due < now;
                return <span className={late ? "font-medium text-red-500" : ""}>{dayjs(r.deposit.due).format("DD/MM")}{late ? " · quá hạn" : ""}</span>;
            },
        },
        {
            title: "Telesales", key: "by", width: 130,
            render: (_, r) => <>{r.deposit.by ?? r.quote.by}<div className="text-[12px] text-gray-500">{r.deposit.receiptAt ? `biên lai ${dayjs(r.deposit.receiptAt).format("HH:mm DD/MM")}` : "chưa gửi biên lai"}</div></>,
        },
        {
            title: "Trạng thái cọc", key: "status", width: 180,
            render: (_, r) => (
                <div className="flex flex-col items-start gap-0.5">
                    <Tag color={DEP_ST[r.deposit.status].color} className="!m-0">{DEP_ST[r.deposit.status].label}</Tag>
                    {r.deposit.status === "waiting" && r.deposit.rejectNote && <span className="text-[11.5px] text-red-500">Không khớp: {r.deposit.rejectNote}</span>}
                    {r.deposit.closeReason && <span className="text-[11.5px] text-gray-500">{r.deposit.closeReason}</span>}
                    {r.deposit.receivedBy && <span className="text-[11.5px] text-gray-500">{r.deposit.receivedBy} · {dayjs(r.deposit.receivedAt).format("HH:mm DD/MM")}{r.deposit.bankRef ? ` · ${r.deposit.bankRef}` : ""}</span>}
                </div>
            ),
        },
        {
            title: "Đơn POS", key: "order", width: 190,
            render: (_, r) => {
                const o = r.deposit.order;
                if (!o) return <span className="text-[12px] text-gray-400">{r.deposit.status === "received" ? "Chưa đẩy" : "—"}</span>;
                return (
                    <div className="flex flex-col items-start gap-0.5">
                        <span className="flex items-center gap-1.5"><b className="font-mono">{o.code}</b><Tag color={ORDER_ST[o.status].color} className="!m-0">{ORDER_ST[o.status].label}</Tag></span>
                        <span className="text-[12px] text-gray-500">Tổng {money(o.total)} · còn thu <b className="text-[#3e91ab]">{money(orderDue(o))}</b></span>
                        <span className="text-[11.5px] text-gray-400">{o.by} · {dayjs(o.at).format("HH:mm DD/MM")}</span>
                    </div>
                );
            },
        },
        {
            title: "Thao tác", key: "act", width: 210, fixed: "right",
            render: (_, r) => {
                const d = r.deposit;
                if (!canAct) return <span className="text-[12px] text-gray-400">Chỉ Kế toán thao tác</span>;
                if (d.status === "waiting" || d.status === "checking") {
                    return (
                        <div className="flex flex-wrap gap-1.5">
                            <AskButton primary label="Đã nhận tiền" title={`Xác nhận đã nhận ${money(d.amount)} — ${d.note}?`} placeholder="Mã giao dịch trên sao kê (không bắt buộc)" onOk={(bankRef) => run(r, { type: "confirm", bankRef }, `Đã nhận cọc ${d.code}`)} />
                            {d.status === "checking" && <AskButton danger label="Không khớp" title="Sao kê chưa có khoản này?" placeholder="Lý do (bắt buộc), VD: chưa thấy tiền về" required onOk={(note) => run(r, { type: "reject", note }, `Đã báo Telesales: ${d.code} chưa khớp sao kê`)} />}
                        </div>
                    );
                }
                if (d.status === "received" && !d.order) {
                    return (
                        <div className="flex flex-wrap gap-1.5">
                            <Popconfirm
                                title={`Đẩy cọc ${d.code} qua POS?`}
                                description={<span className="text-[12.5px]">Tạo đơn hàng cọc theo {r.quote.code}: {r.quote.items.map((x) => x.name).join(" + ")} · đã cọc {money(d.amount)}</span>}
                                okText="Đẩy qua POS"
                                cancelText="Không"
                                onConfirm={() => run(r, { type: "push" }, `Đã đẩy cọc ${d.code} qua POS`)}
                            >
                                <Button size="small" type="primary" className="!bg-[#3e91ab]" icon={<Send size={13} />}>Đẩy qua POS</Button>
                            </Popconfirm>
                            <AskButton label="Hoàn cọc" title={`Hoàn ${money(d.amount)} cho khách?`} placeholder="Lý do hoàn (bắt buộc)" required onOk={(reason) => run(r, { type: "refund", reason }, `Đã hoàn cọc ${d.code}`)} />
                            <AskButton danger label="Khách bỏ cọc" title={`Ghi nhận khách bỏ cọc ${money(d.amount)}?`} placeholder="Lý do (bắt buộc), VD: không đến, quá hạn giữ chỗ" required onOk={(reason) => run(r, { type: "forfeit", reason }, `Đã ghi nhận khách bỏ cọc ${d.code}`)} />
                        </div>
                    );
                }
                return <span className="text-[12px] text-gray-400">{d.order ? "POS xử lý tiếp" : "—"}</span>;
            },
        },
    ];

    return (
        <Layout>
            <Helmet><title>Đối soát cọc | Khothi</title></Helmet>
            <div className="space-y-4 pb-16 text-sm">
                <div className="flex min-w-0 items-center gap-3">
                    <Link to="/telesales/customer" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e8f4f7] text-[#3e91ab] hover:bg-[#d5ebf1] dark:bg-[#3e91ab]/20" aria-label="Về Khách tiềm năng">
                        <ArrowLeft size={18} />
                    </Link>
                    <div className="min-w-0">
                        <h1 className="m-0 text-[18px] font-bold leading-tight text-gray-900 dark:text-gray-50">Đối soát cọc</h1>
                        <div className="text-[12.5px] text-gray-500 dark:text-gray-400">Khớp sao kê theo nội dung chuyển khoản → xác nhận đã nhận cọc → đẩy qua POS thành đơn hàng cọc</div>
                    </div>
                </div>

                <Alert type="warning" showIcon className="text-[12.5px]" message="Tạo cọc, báo biên lai, xác nhận đã lưu trên máy chủ. Đẩy qua POS, hoàn, bỏ cọc mới lưu trên máy này (chưa có API), chưa gửi tới POS. Danh sách chỉ có khoản đã được mở trong hồ sơ khách trên máy này." />

                <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                    <Kpi icon={<Receipt size={15} />} tone="amber" label="Cần đối soát" value={stats.check.length} sub={money(sum(stats.check, (r) => r.deposit.amount))} alert={stats.check.length > 0} onClick={() => setSt("check")} active={st === "check"} />
                    <Kpi icon={<Clock3 size={15} />} tone="slate" label="Chờ chuyển khoản" value={stats.waiting.length} sub={`${money(sum(stats.waiting, (r) => r.deposit.amount))}${overdue ? ` · ${overdue} quá hạn` : ""}`} onClick={() => setSt("waiting")} active={st === "waiting"} />
                    <Kpi icon={<Send size={15} />} tone="green" label="Đã nhận · chờ đẩy POS" value={stats.push.length} sub={money(sum(stats.push, (r) => r.deposit.amount))} onClick={() => setSt("push")} active={st === "push"} />
                    <Kpi icon={<ShoppingBag size={15} />} tone="sky" label="Đã đẩy qua POS" value={stats.pushed.length} sub={`Cọc ${money(sum(stats.pushed, (r) => r.deposit.amount))}`} onClick={() => setSt("pushed")} active={st === "pushed"} />
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                        <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Mã cọc, mã đơn, nội dung CK, tên, SĐT" value={q} onChange={(e) => setQ(e.target.value)} className="w-[280px]" />
                        <Segmented value={st} onChange={(v) => setSt(v as St)} className="max-w-full overflow-x-auto" options={[
                            { value: "check", label: `Cần đối soát ${stats.check.length}` },
                            { value: "waiting", label: `Chờ chuyển khoản ${stats.waiting.length}` },
                            { value: "push", label: `Chờ đẩy POS ${stats.push.length}` },
                            { value: "pushed", label: `Đã đẩy POS ${stats.pushed.length}` },
                            { value: "closed", label: `Hoàn · bỏ cọc ${rows.filter(ST_PASS.closed).length}` },
                            { value: "all", label: `Tất cả ${rows.length}` },
                        ]} />
                    </div>
                    <Table<DepositRow>
                        rowKey={(r) => `${r.leadId}-${r.quote.id}`}
                        columns={columns}
                        dataSource={shown}
                        size="middle"
                        scroll={{ x: 1380 }}
                        pagination={{ pageSize: 20, hideOnSinglePage: true, showTotal: (t) => `${t} khoản cọc` }}
                        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={st === "check" ? "Không có khoản nào chờ đối soát" : "Không có khoản cọc ở mục này"} /> }}
                    />
                </div>
            </div>
        </Layout>
    );
}
